const productCache = new Map();

class BlockBuilderCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartButton = document.querySelector('block-builder #bb-cart-button');
    this.cartIcon = document.querySelector('block-builder #bb-cart-icon');
    this.cartDrawerContent = this.querySelector('#bb-cart-drawer-content');
    this.cartItemTemplate = this.querySelector('#bb-cart-item-template');
    this.reviewItems = this.closest('block-builder').querySelector('#bb-review-items');
  }

  cartItems = null;
  isOpen = false;

  connectedCallback() {
    this.cartButton.addEventListener('click', this.toggleCartDrawer.bind(this));
    
    this.addEventListener('click', this.handleRemoveItemClick.bind(this));
    this.reviewItems.addEventListener('click', this.handleRemoveReviewItemClick.bind(this));
  }

  // Create cache key from product handle and variant ID
  getCacheKey(handle, variantId) {
    return `${handle}-${variantId}`;
  }

  async renderCartItems() {
    this.cartButton.classList.add('is-loading');
    
    if (!this.cartItems || this.cartItems.length === 0) {
      this.cartButton.classList.remove('is-loading');
      return;
    }

    try {
      // Fetch products that aren't already cached
      const uncachedItems = this.cartItems.filter(item => {
        const cacheKey = this.getCacheKey(item.handle, item.variant_id);
        return !productCache.has(cacheKey);
      });
      
      if (uncachedItems.length > 0) {
        const fetchPromises = uncachedItems.map(item => 
          fetch(window.Shopify.routes.root + `products/${item.handle}.js`)
            .then(response => response.json())
            .then(data => {
              const cacheKey = this.getCacheKey(item.handle, item.variant_id);
              productCache.set(cacheKey, data);
              return data;
            })
        );
        
        await Promise.all(fetchPromises);
      }

      // Now render all cart items using cached data
      this.renderCartItemsFromCache();
      
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      this.cartButton.classList.remove('is-loading');
    }
  }

  renderCartItemsFromCache() {
    // Clear existing content
    this.cartDrawerContent.innerHTML = '';
    
    this.cartItems.forEach(item => {
      const cacheKey = this.getCacheKey(item.handle, item.variant_id);
      const productData = productCache.get(cacheKey);
      if (productData && item.quantity > 0) {
        const cartItemElement = this.createCartItemElement(productData, item);
        this.cartDrawerContent.appendChild(cartItemElement);
      }
    });
  }

  createCartItemElement(productData, cartItem) {
    const template = this.cartItemTemplate.content.cloneNode(true);
    
    // Find the specific variant
    const variant = productData.variants.find(v => v.id === cartItem.variantId);
    
    // Set product image
    const img = template.querySelector('img');
    if (variant && variant.featured_image) {
      img.src = this.generateThumbnailUrl(variant.featured_image.src);
      img.alt = variant.title || productData.title;
    } else if (productData.featured_image) {
      img.src = this.generateThumbnailUrl(productData.featured_image);
      img.alt = productData.title;
    }
    
    // Set product title
    const titleSlot = template.querySelector('[name="title"]');
    const variantTitle = productData.title;
    titleSlot.textContent = variantTitle;
    
    // Set price
    const priceSlot = template.querySelector('[name="price"]');
    const pricePerUnit = variant ? variant.price : productData.price;
    const totalPrice = pricePerUnit * cartItem.quantity;
    priceSlot.textContent = Shopify.formatMoney(totalPrice);
    
    // Set quantity
    const quantitySlot = template.querySelector('[name="quantity"]');
    quantitySlot.textContent = cartItem.quantity;
    
    // Set price per unit
    const pricePerUnitSlot = template.querySelector('[name="price-per-unit"]');
    pricePerUnitSlot.textContent = Shopify.formatMoney(pricePerUnit);

    // Set variant id on remove button
    const removeButton = template.querySelector('[data-bb-remove-cart-item]');
    removeButton.dataset.variantId = cartItem.variantId;
    const removeLabelTemplate = removeButton.dataset.removeLabelTemplate || 'Remove __PRODUCT__';
    removeButton.setAttribute('aria-label', removeLabelTemplate.replace('__PRODUCT__', variantTitle));

    if (cartItem.type === 'block') {
      const changeBlockText = removeButton.dataset.bbChangeBlockText;
      removeButton.classList.add('bb-cart-item-change-button');
      removeButton.removeAttribute('data-bb-remove-cart-item');
      removeButton.setAttribute('data-bb-change-block', '');
      removeButton.textContent = changeBlockText;
      removeButton.addEventListener('click', this.handleChangeBlockClick.bind(this));
    }
    
    return template;
  }

  generateThumbnailUrl(imageUrl) {
    return imageUrl + '&width=154';
  }

  toggleCartDrawer() {
    if (!this.isOpen) {
      this.renderCartItems().then(() => {
        this.classList.remove('hidden');
        this.cartIcon.classList.add('[&_svg]:rotate-180');
        this.isOpen = true;
      });
    } else {
      this.classList.add('hidden');
      this.cartIcon.classList.remove('[&_svg]:rotate-180');
      this.isOpen = false;
    }
  }

  async handleCartItemsUpdate() {
    if (this.isOpen) {
      await this.renderCartItems();
    }

    if (this.cartItems && this.cartItems.length > 0) {
      this.cartButton.disabled = false;
    } else {
      this.cartButton.disabled = true;
    }
  }

  handleRemoveItemClick(event, isReviewPage = false) {
    if (event.target.closest('[data-bb-remove-cart-item]')) {
      const variantId = event.target.closest('[data-bb-remove-cart-item]').dataset.variantId;
      const cartItemElement = event.target.closest('.bb-cart-item');
      if (cartItemElement && variantId) {
        this.removeCartItem(cartItemElement, variantId, isReviewPage);
      }
    }

    if (event.target.closest('[data-bb-change-block]')) {
      event.preventDefault();
      this.handleChangeBlockClick(event);
    }
  }

  handleChangeBlockClick(event) {
    this.dispatchEvent(new CustomEvent('change-block', {
      detail: {
        variantId: event.target.dataset.variantId
      },
      bubbles: true
    }));
  }

  handleRemoveReviewItemClick(event) {
    this.handleRemoveItemClick(event, true);
  }

  async removeCartItem(cartItemElement, variantId, isReviewPage = false) {
    const cartItems = Array.from(this.cartDrawerContent.children);
    const itemIndex = isReviewPage ? Array.from(this.reviewItems.children).indexOf(cartItemElement) : cartItems.indexOf(cartItemElement);
    
    if (itemIndex !== -1 && this.cartItems && this.cartItems[itemIndex]) {
      try {
        cartItemElement.remove();
        
        this.dispatchEvent(new CustomEvent('cart-item-removed', {
          detail: { 
            variantId: variantId,
            isReviewPage: isReviewPage
          },
          bubbles: true
        }));
        
      } catch (error) {
        console.error('Error removing cart item:', error);
      }
    }
  }
}

customElements.define('block-builder-cart-drawer', BlockBuilderCartDrawer);